// src/pages/TradeTracker/utils/useTradesData.js
import { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "lib/dayjs";
import { supabase } from "services/supabaseClient";
import { norm } from "./formatters";

function toInCsv(values) {
  const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
  return values.map((v) => esc(v)).join(",");
}

export default function useTradesData(
  profile,
  isAdmin,
  selectedOrgId,
  selectedParty
) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [availableParties, setAvailableParties] = useState([]);
  const [trades, setTrades] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");

  const [portfolioSeries, setPortfolioSeries] = useState([]);
  const [seriesFrom, setSeriesFrom] = useState("");
  const [seriesTo, setSeriesTo] = useState("");

  const tradesById = useMemo(() => {
    const m = new Map();
    for (const t of trades) m.set(norm(t.trade_id), t);
    return m;
  }, [trades]);

  // 1) Orgs list
  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase
        .from("orgs")
        .select("id, name")
        .order("name");

      if (ignore) return;
      if (err) {
        setError(err.message || err);
        setLoading(false);
        return;
      }

      setAvailableOrgs(data || []);
      setLoading(false);
    })();

    return () => {
      ignore = true;
    };
  }, [profile?.id, isAdmin]);

  // Helper
  async function fetchLatestDateForTrades(tradeIds = []) {
    if (!tradeIds.length) return "";
    const { data } = await supabase
      .from("trade_valuations")
      .select("valuation_date")
      .in("trade_id", tradeIds)
      .order("valuation_date", { ascending: false })
      .limit(1)
      .maybeSingle();
    return data?.valuation_date || "";
  }

  // Helper
  async function fetchValuationsForDate(tradeIds = [], ymd) {
    if (!tradeIds.length || !ymd) return [];
    const { data } = await supabase
      .from("trade_valuations")
      .select(
        "trade_id, valuation_date, clean_value, accrued, total_value, pv01, notional_amount"
      )
      .eq("valuation_date", ymd)
      .in("trade_id", tradeIds)
      .limit(5000);
    return data || [];
  }

  // Aggregated series RPC
  const loadSeriesRange = useCallback(
    async (fromYmd, toYmd, tradeIds) => {
      if (!tradeIds?.length || !fromYmd || !toYmd) {
        setPortfolioSeries([]);
        setSeriesFrom(fromYmd || "");
        setSeriesTo(toYmd || "");
        return;
      }

      const from = dayjs(fromYmd);
      const to = dayjs(toYmd);
      const diffDays = to.diff(from, "day");
      if (diffDays > 366 * 5) {
        setPortfolioSeries([]);
        setSeriesFrom(fromYmd);
        setSeriesTo(toYmd);
        return;
      }

      const { data } = await supabase.rpc("portfolio_series_by_trades", {
        trade_ids: tradeIds,
        from_date: fromYmd,
        to_date: toYmd,
      });

      const mapped = (data || []).map((r) => ({
        ts: dayjs(r.valuation_date).startOf("day").valueOf(),
        valuation: Number(r.valuation) || 0,
        notional: Number(r.notional) || 0,
        pv01: Number(r.pv01) || 0,
      }));

      setPortfolioSeries(mapped);
      setSeriesFrom(fromYmd);
      setSeriesTo(toYmd);
    },
    []
  );

  // Load parties/trades then valuations + series
  useEffect(() => {
    let ignore = false;

    async function runPipeline(tradeRowsPromise) {
      setLoading(true);
      setError(null);

      const { data: tradeRows, error: tradeErr } = await tradeRowsPromise;
      if (ignore) return;

      if (tradeErr) {
        setError(tradeErr.message || tradeErr);
        setLoading(false);
        return;
      }

      let filteredTrades = tradeRows || [];
      if (selectedParty) {
        filteredTrades = filteredTrades.filter(
          (t) =>
            t.leg1_party === selectedParty || t.leg2_party === selectedParty
        );
      }

      const tradeIds = [...new Set(filteredTrades.map((r) => r.trade_id))];
      const latest = await fetchLatestDateForTrades(tradeIds);

      let dayVals = [];
      if (latest) {
        dayVals = await fetchValuationsForDate(tradeIds, latest);
      }

      let to, from;

      if (seriesFrom && seriesTo) {
        from = seriesFrom;
        to = seriesTo;
      } else {
        to = latest || dayjs().format("YYYY-MM-DD");
        from = dayjs(to).subtract(89, "day").format("YYYY-MM-DD");
      }

      await loadSeriesRange(from, to, tradeIds);

      if (!ignore) {
        setTrades(filteredTrades);
        setValuations(dayVals || []);

        let effectiveDate = selectedDate || latest || "";
        if (selectedDate && dayVals.length === 0) {
          effectiveDate = latest || "";
        }
        setSelectedDate(effectiveDate);

        setLoading(false);
      }
    }

    async function loadForAdminAll() {
      const { data: partyRows } = await supabase
        .from("parties")
        .select("id, canon_name")
        .order("canon_name");

      if (!ignore) setAvailableParties(partyRows || []);

      const tradeRowsPromise = supabase
        .from("trades")
        .select(
          `trade_id, trade_type, trade_date, effective_date, termination_date,
           leg1_party, leg1_pay, leg1_rate, leg1_spread, leg1_leverage,
           leg1_daycount, leg1_payfreq, leg1_paydate, leg1_index, leg1_strike,
           leg2_party, leg2_pay, leg2_rate, leg2_spread, leg2_leverage,
           leg2_daycount, leg2_payfreq, leg2_paydate, leg2_index, leg2_strike,
           notional_units,
           record_doc_scopes ( record_docs ( id, title, file_path ) )`
        )
        .order("trade_date", { ascending: false });

      await runPipeline(tradeRowsPromise);
    }

    async function loadForSpecificOrg(orgId) {
      const { data: cplRows } = await supabase
        .from("org_party_links")
        .select("party_id")
        .eq("org_id", orgId);
      const partyIds = [...new Set((cplRows || []).map((r) => r.party_id))];

      if (!partyIds.length) {
        if (!ignore) {
          setAvailableParties([]);
          setTrades([]);
          setValuations([]);
          setSelectedDate("");
          setPortfolioSeries([]);
          setSeriesFrom("");
          setSeriesTo("");
          setLoading(false);
        }
        return;
      }

      const { data: partyRows } = await supabase
        .from("parties")
        .select("id, canon_name")
        .in("id", partyIds);

      const { data: aliasRows } = await supabase
        .from("party_aliases")
        .select("party_id, alias")
        .in("party_id", partyIds);

      const aliasSet = new Set((partyRows || []).map((p) => p.canon_name));
      for (const a of aliasRows || []) if (a.alias) aliasSet.add(a.alias);

      if (!ignore) setAvailableParties(partyRows || []);

      const aliases = [...aliasSet];
      const inCsv = toInCsv(aliases);

      const tradeRowsPromise = supabase
        .from("trades")
        .select(
          `trade_id, trade_type, trade_date, effective_date, termination_date,
           leg1_party, leg1_pay, leg1_rate, leg1_spread, leg1_leverage,
           leg1_daycount, leg1_payfreq, leg1_paydate, leg1_index, leg1_strike,
           leg2_party, leg2_pay, leg2_rate, leg2_spread, leg2_leverage,
           leg2_daycount, leg2_payfreq, leg2_paydate, leg2_index, leg2_strike,
           notional_units,
           record_doc_scopes ( record_docs ( id, title, file_path ) )`
        )
        .or(`leg1_party.in.(${inCsv}),leg2_party.in.(${inCsv})`);

      await runPipeline(tradeRowsPromise);
    }

    async function loadForAllOrgsForUser() {
      const { data: culRows } = await supabase
        .from("org_user_links")
        .select("org_id")
        .eq("user_id", profile.id);
      const orgIds = (culRows || []).map((r) => r.org_id);

      if (!orgIds.length) {
        if (!ignore) {
          setAvailableParties([]);
          setTrades([]);
          setValuations([]);
          setSelectedDate("");
          setPortfolioSeries([]);
          setSeriesFrom("");
          setSeriesTo("");
          setLoading(false);
        }
        return;
      }

      const { data: cplRows } = await supabase
        .from("org_party_links")
        .select("party_id")
        .in("org_id", orgIds);
      const partyIds = [...new Set((cplRows || []).map((r) => r.party_id))];

      if (!partyIds.length) {
        if (!ignore) {
          setAvailableParties([]);
          setTrades([]);
          setValuations([]);
          setSelectedDate("");
          setPortfolioSeries([]);
          setSeriesFrom("");
          setSeriesTo("");
          setLoading(false);
        }
        return;
      }

      const { data: partyRows } = await supabase
        .from("parties")
        .select("id, canon_name")
        .in("id", partyIds);

      const { data: aliasRows } = await supabase
        .from("party_aliases")
        .select("party_id, alias")
        .in("party_id", partyIds);

      const aliasSet = new Set((partyRows || []).map((p) => p.canon_name));
      for (const a of aliasRows || []) if (a.alias) aliasSet.add(a.alias);

      if (!ignore) setAvailableParties(partyRows || []);

      const aliases = [...aliasSet];
      const inCsv = toInCsv(aliases);

      const tradeRowsPromise = supabase
        .from("trades")
        .select(
          `trade_id, trade_type, trade_date, effective_date, termination_date,
           leg1_party, leg1_pay, leg1_rate, leg1_spread, leg1_leverage,
           leg1_daycount, leg1_payfreq, leg1_paydate, leg1_index, leg1_strike,
           leg2_party, leg2_pay, leg2_rate, leg2_spread, leg2_leverage,
           leg2_daycount, leg2_payfreq, leg2_paydate, leg2_index, leg2_strike,
           notional_units,
           record_doc_scopes ( record_docs ( id, title, file_path ) )`
        )
        .or(`leg1_party.in.(${inCsv}),leg2_party.in.(${inCsv})`);

      await runPipeline(tradeRowsPromise);
    }

    if (!selectedOrgId) {
      if (isAdmin) loadForAdminAll();
      else loadForAllOrgsForUser();
      return () => {
        ignore = true;
      };
    }

    loadForSpecificOrg(selectedOrgId);

    return () => {
      ignore = true;
    };
  }, [
    selectedOrgId,
    isAdmin,
    profile?.id,
    loadSeriesRange,
    selectedParty,
    selectedDate,
  ]);

  // Refresh valuations when date changes
  useEffect(() => {
    let ignore = false;

    async function refreshVals() {
      if (!selectedDate) return;

      let workTrades = trades;
      if (selectedParty) {
        workTrades = workTrades.filter(
          (t) =>
            t.leg1_party === selectedParty || t.leg2_party === selectedParty
        );
      }

      const tradeIds = workTrades.map((r) => r.trade_id);
      if (!tradeIds.length) return;

      const vals = await fetchValuationsForDate(tradeIds, selectedDate);
      if (!ignore) setValuations(vals || []);
    }

    refreshVals();
    return () => {
      ignore = true;
    };
  }, [selectedDate, trades, selectedParty]);

  // Refresh series N days
  const loadSeriesLastNDays = useCallback(
    (days = 60) => {
      const to = selectedDate || dayjs().format("YYYY-MM-DD");
      const from = dayjs(to).subtract(days - 1, "day").format("YYYY-MM-DD");

      let workTrades = trades;
      if (selectedParty) {
        workTrades = workTrades.filter(
          (t) =>
            t.leg1_party === selectedParty || t.leg2_party === selectedParty
        );
      }

      const tradeIds = [...new Set(workTrades.map((r) => r.trade_id))];
      return loadSeriesRange(from, to, tradeIds);
    },
    [selectedDate, trades, selectedParty, loadSeriesRange]
  );

  return {
    loading,
    error,
    availableOrgs,
    availableParties,
    trades,
    valuations,
    tradesById,
    selectedDate,
    setSelectedDate,
    portfolioSeries,
    seriesFrom,
    seriesTo,
    loadSeriesRange,
    loadSeriesLastNDays,
  };
}
